import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { CheckSquare, Plus, AlertCircle, Network, Edit, Trash2, User, Eye } from 'lucide-react';
import { LinkManager } from '@/components/shared/LinkManager';
import { EntityLinkedPreview } from '@/components/shared/EntityLinkedPreview';
import { ControlEditDialog } from '@/components/controls/ControlEditDialog';
import { ControlCreateDialog } from '@/components/controls/ControlCreateDialog';
import { useControls } from '@/hooks/queries';
import { useLinkStore } from '@/stores/linkStore';

const statusColors = {
  implemented: 'bg-success text-success-foreground',
  not_implemented: 'bg-danger text-danger-foreground',
  partially_implemented: 'bg-warning text-warning-foreground',
  not_applicable: 'bg-muted text-muted-foreground',
};

const statusLabels = {
  implemented: 'Implemented',
  not_implemented: 'Not Implemented',
  partially_implemented: 'Partially Implemented', 
  not_applicable: 'Not Applicable',
};

export default function ControlsPage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const { loadLinks } = useLinkStore();

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const {
    controls,
    isLoading,
    createControl,
    updateControl,
    deleteControl,
    isCreating,
    isDeleting,
  } = useControls(organization?.id, { status: statusFilter !== 'all' ? statusFilter : undefined, domain: domainFilter !== 'all' ? domainFilter : undefined });

  const selectedControl = controls?.find(c => c.id === selectedControlId);

  const handleCreate = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!organization?.id) return;
    
    createControl({
      ...data,
      org_id: organization.id,
    }, {
      onSuccess: async (newControl: any) => {
        if (newControl?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('control', newControl.id, link.type, [link.id]);
          }
        }
      }
    });
    
    setAddDialogOpen(false);
  };

  const handleDelete = () => {
    if (!selectedControlId) return;
    deleteControl(selectedControlId);
    setDeleteDialogOpen(false);
    setSelectedControlId(null);
    setDetailSheetOpen(false);
  };

  const openDetailSheet = (controlId: string) => {
    setSelectedControlId(controlId);
    setDetailSheetOpen(true);
  };

  const uniqueDomains = controls ? [...new Set(controls.map(c => c.domain).filter(Boolean))] : [];

  // Filter controls by owner if assigned filter is active
  const filteredControls = controls?.filter(control => {
    if (filterAssigned && control.owner_id !== profile?.user_id) return false;
    return true;
  }) || [];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Controls</h1>
            <p className="text-muted-foreground">Manage security and compliance controls</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Controls</h1>
          <p className="text-muted-foreground">Manage security and compliance controls</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Control
        </Button>
      </div>

      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="implemented">Implemented</SelectItem>
            <SelectItem value="not_implemented">Not Implemented</SelectItem>
            <SelectItem value="partially_implemented">Partially Implemented</SelectItem>
            <SelectItem value="not_applicable">Not Applicable</SelectItem>
          </SelectContent>
        </Select>

        <Select value={domainFilter} onValueChange={setDomainFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by domain" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Domains</SelectItem>
            {uniqueDomains.map((domain) => (
              <SelectItem key={domain} value={domain}>
                {domain}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      {filteredControls && filteredControls.length > 0 ? (
        <div className="space-y-4">
          {filteredControls.map((control) => (
            <Card 
              key={control.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openDetailSheet(control.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckSquare className="h-5 w-5 text-foreground" />
                      <CardTitle className="text-lg">{control.control_id}</CardTitle>
                      <Badge className={statusColors[control.status as keyof typeof statusColors]}>
                        {statusLabels[control.status as keyof typeof statusLabels]}
                      </Badge>
                      {control.due_date && new Date(control.due_date) < new Date() && (
                        <AlertCircle className="h-4 w-4 text-danger" />
                      )}
                    </div>
                    <CardDescription>{control.title}</CardDescription>
                    {/* Show linked entities */}
                    <div className="mt-2">
                      <EntityLinkedPreview entityType="control" entityId={control.id} maxDisplay={3} />
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetailSheet(control.id)}
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedControlId(control.id);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedControlId(control.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {(control as any).owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{(control as any).owner.name || (control as any).owner.email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Domain</p>
                    <p className="font-medium">{control.domain}</p>
                  </div>
                  {control.due_date && (
                    <div>
                      <p className="text-muted-foreground">Due Date</p>
                      <p className="font-medium">{new Date(control.due_date).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckSquare className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No controls found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start adding security controls to track your compliance status.
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Control
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5" />
                Control Details
              </SheetTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDetailSheetOpen(false);
                    setEditDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </SheetHeader>

          {selectedControl && (
            <div className="space-y-6 py-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckSquare className="h-5 w-5 text-foreground" />
                  <h3 className="text-lg font-semibold">{selectedControl.control_id}</h3>
                </div>
                <h4 className="text-sm font-medium mb-2">{selectedControl.title}</h4>
                <Badge className={statusColors[selectedControl.status as keyof typeof statusColors]}>
                  {statusLabels[selectedControl.status as keyof typeof statusLabels]}
                </Badge>
              </div>

              {selectedControl.description && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground">{selectedControl.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Domain</p>
                  <p className="font-medium">{selectedControl.domain}</p>
                </div>
                {selectedControl.due_date && (
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">
                      {new Date(selectedControl.due_date).toLocaleDateString()}
                      {new Date(selectedControl.due_date) < new Date() && (
                        <span className="text-danger ml-2">(Overdue)</span>
                      )}
                    </p>
                  </div>
                )}
              </div>

              {selectedControl.gap_statement && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Gap Statement</h4>
                  <p className="text-sm text-danger">{selectedControl.gap_statement}</p>
                </div>
              )}

              {selectedControl.remediation && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Remediation</h4>
                  <p className="text-sm text-muted-foreground">{selectedControl.remediation}</p>
                </div>
              )}

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Related Entities</h4>
                </div>
                <LinkManager 
                  entityType="control"
                  entityId={selectedControl.id}
                  entityName={selectedControl.control_id}
                  showHeader={false}
                  allowedTypes={['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'risk', 'evidence', 'supplier', 'incident', 'task']}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ControlCreateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSave={handleCreate}
        isCreating={isCreating}
      />

      <ControlEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        control={selectedControl}
        onSave={(id, data) => updateControl({ id, data })}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Control</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedControl?.title}"? This action cannot be undone and will remove all associated links.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-danger text-danger-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
